import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { LOGIN } from "@/constants/testIds";
import { toast } from "sonner";
import { Phone, KeyRound } from "lucide-react";

const ADMIN_PHONE = "7680951069";

export default function LoginPage() {
  const { requestOtp, verifyOtp, verifyAdminPin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loginMethod, setLoginMethod] = useState("otp");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isAdminPhone = phone === ADMIN_PHONE;

  const sendOtp = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{10}$/.test(phone)) {
      setError("Enter a 10-digit phone number");
      return;
    }
    setLoading(true);
    try {
      const result = await requestOtp(phone);
      setLoginMethod(result.method || "otp");
      setOtp("");
      setStep(2);
      toast.success(result.method === "pin" ? "Enter admin PIN" : "OTP sent");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = loginMethod === "pin"
        ? await verifyAdminPin(phone, otp)
        : await verifyOtp(phone, otp);
      toast.success(`Welcome, ${u.name}`);
      if (u.role === "admin") navigate("/admin");
      else navigate(from, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={LOGIN.page} className="min-h-screen grid place-items-center px-4 py-10">
      <div className="w-full max-w-sm bg-hyd-panel border border-hyd-border rounded-2xl p-6 shadow-card-dark">
        <div className="flex justify-between items-center mb-6">
          <Logo />
          <Link to="/" className="text-xs text-hyd-text2 hover:text-hyd-gold">Back</Link>
        </div>

        <h1 className="font-display text-3xl font-extrabold">
          {step === 1 ? "Sign in" : loginMethod === "pin" ? "Admin sign in" : "Verify OTP"}
        </h1>
        <p className="text-hyd-text2 text-sm mt-1">
          {step === 1
            ? "Enter your phone to continue."
            : loginMethod === "pin"
              ? "Enter your admin PIN."
              : `Sent to +91 ${phone}`}
        </p>

        {step === 1 ? (
          <form onSubmit={sendOtp} className="mt-6 space-y-4">
            <div>
              <Label className="text-xs text-hyd-text2 mb-1 block flex items-center gap-1">
                <Phone className="w-3 h-3" /> Phone number
              </Label>
              <div className="flex">
                <span className="grid place-items-center px-3 rounded-l-md border border-r-0 border-hyd-border bg-hyd-bg text-hyd-text2 text-sm">
                  +91
                </span>
                <Input
                  data-testid={LOGIN.phoneInput}
                  inputMode="numeric"
                  placeholder="10-digit number"
                  value={phone}
                  maxLength={10}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  className="rounded-l-none bg-hyd-bg border-hyd-border text-hyd-text text-lg tracking-widest"
                />
              </div>
            </div>
            {error && <div className="text-hyd-red text-sm">{error}</div>}
            <Button
              data-testid={LOGIN.sendOtpBtn}
              type="submit"
              disabled={loading}
              className="w-full bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold uppercase tracking-wider"
            >
              {loading ? (isAdminPhone ? "Continuing..." : "Sending...") : (isAdminPhone ? "Continue with PIN" : "Send OTP")}
            </Button>
          </form>
        ) : (
          <form onSubmit={verify} className="mt-6 space-y-4">
            <div>
              <Label className="text-xs text-hyd-text2 mb-1 block flex items-center gap-1">
                <KeyRound className="w-3 h-3" /> {loginMethod === "pin" ? "Admin PIN" : "One-time password"}
              </Label>
              <Input
                data-testid={LOGIN.otpInput}
                inputMode="numeric"
                placeholder={loginMethod === "pin" ? "6-digit PIN" : "6-digit OTP"}
                value={otp}
                maxLength={6}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="bg-hyd-bg border-hyd-border text-hyd-text text-2xl tracking-[0.5em] text-center"
              />
            </div>
            {error && <div className="text-hyd-red text-sm">{error}</div>}
            <Button
              data-testid={LOGIN.verifyBtn}
              type="submit"
              disabled={loading}
              className="w-full bg-hyd-red hover:bg-hyd-redDim text-white font-bold uppercase tracking-wider"
            >
              {loading ? "Verifying..." : "Verify & continue"}
            </Button>
            <button
              type="button"
              onClick={() => {
                setStep(1);
                setError("");
              }}
              className="w-full text-xs text-hyd-text2 hover:text-hyd-gold"
            >
              Change phone
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
